import { getNursePatients } from "../../features/schedule/derivedAssignments"
import {
	getStartTime,
	getEndTime,
	rangesOverlap,
	confirmedCancellationNoShow
} from "../../utils/realTimeUtils"

// Weight on patient count vs total acuity in the scoring function. One extra
// patient costs the same as ALPHA acuity points. Tune as nurse feedback comes in.
const ALPHA = 2.5

// A patient is "bumpable" — i.e. okay to displace from its current nurse — if
// it isn't already in treatment and isn't a 1-to-1.
const isBumpable = (patient, oneToOneLevel) =>
	patient.internalStatus !== "Ongoing" && patient.acuity < oneToOneLevel

const overlapsInTime = (a, b, sandbox, isTomorrowMode) =>
	rangesOverlap(
		[getStartTime(a, sandbox, isTomorrowMode), getEndTime(a, sandbox, isTomorrowMode)],
		[getStartTime(b, sandbox, isTomorrowMode), getEndTime(b, sandbox, isTomorrowMode)]
	)

/**
 * Returns true if `nurse` can take `patient` given the working schedule.
 * Shift fit and 1-to-1 conflict are always enforced. Hard caps from settings
 * (maxAcuityForNurse, maxPatientsForNurse) are enforced when applyHardCaps is true.
 */
const canAccept = ({
	nurse,
	patient,
	workingSchedule,
	settings,
	sandbox,
	isTomorrowMode,
	applyHardCaps
}) => {
	const patientStart = getStartTime(patient, sandbox, isTomorrowMode)
	const patientEnd = getEndTime(patient, sandbox, isTomorrowMode)
	if (patientStart < nurse.scheduledStartTime) return false
	if (patientEnd > nurse.scheduledEndTime) return false

	const existing = getNursePatients(workingSchedule, nurse.nurseId).filter(
		p => !confirmedCancellationNoShow(p)
	)

	const oneToOneLevel = settings.oneToOneAcuityLevel ?? 5
	for (const other of existing) {
		const eitherIsOneToOne =
			other.acuity === oneToOneLevel || patient.acuity === oneToOneLevel
		if (eitherIsOneToOne && overlapsInTime(other, patient, sandbox, isTomorrowMode)) {
			return false
		}
	}

	if (applyHardCaps) {
		const totalAcuityAfter =
			existing.reduce((s, p) => s + p.acuity, 0) + patient.acuity
		const countAfter = existing.length + 1
		if (totalAcuityAfter > settings.maxAcuityForNurse) return false
		if (countAfter > settings.maxPatientsForNurse) return false
	}

	return true
}

/** Lower score = better target. */
const scoreIfAssigned = ({ nurse, patient, workingSchedule }) => {
	const existing = getNursePatients(workingSchedule, nurse.nurseId).filter(
		p => !confirmedCancellationNoShow(p)
	)
	const totalAcuityAfter = existing.reduce((s, p) => s + p.acuity, 0) + patient.acuity
	const countAfter = existing.length + 1
	return totalAcuityAfter + ALPHA * countAfter
}

/**
 * Returns the patients currently on `nurse` that overlap `patient`'s time window
 * (and aren't cancelled/no-show).
 */
const getOverlappersOnNurse = ({ nurse, patient, workingSchedule, sandbox, isTomorrowMode }) =>
	getNursePatients(workingSchedule, nurse.nurseId)
		.filter(p => !confirmedCancellationNoShow(p))
		.filter(p => overlapsInTime(p, patient, sandbox, isTomorrowMode))

/**
 * Picks the best nurse for `patient`. Returns
 *   { nurseId, patientsToBump: [] } if a no-conflict nurse exists,
 *   { nurseId, patientsToBump: [...] } if a nurse exists where all overlappers
 *     are bumpable (only when allowBumping is true),
 *   null otherwise.
 *
 * First tries with hard caps; on no fit, retries with caps disabled.
 */
const pickNurseFor = ({
	patient,
	candidateNurses,
	workingSchedule,
	settings,
	sandbox,
	isTomorrowMode,
	allowBumping
}) => {
	for (const applyHardCaps of [true, false]) {
		const valid = candidateNurses.filter(nurse =>
			canAccept({
				nurse,
				patient,
				workingSchedule,
				settings,
				sandbox,
				isTomorrowMode,
				applyHardCaps
			})
		)
		if (valid.length === 0) continue

		const annotated = valid.map(nurse => ({
			nurse,
			overlappers: getOverlappersOnNurse({
				nurse,
				patient,
				workingSchedule,
				sandbox,
				isTomorrowMode
			}),
			score: scoreIfAssigned({ nurse, patient, workingSchedule })
		}))

		// First preference: a nurse with no time overlap on this patient.
		const noOverlap = annotated.filter(x => x.overlappers.length === 0)
		if (noOverlap.length > 0) {
			const best = noOverlap.reduce((a, b) => (a.score <= b.score ? a : b))
			return { nurseId: best.nurse.nurseId, patientsToBump: [] }
		}

		if (!allowBumping) continue

		const oneToOneLevel = settings.oneToOneAcuityLevel ?? 5
		const bumpable = annotated.filter(x =>
			x.overlappers.every(o => isBumpable(o, oneToOneLevel))
		)
		if (bumpable.length === 0) continue

		// Among nurses where overlappers can be displaced, prefer the fewest
		// displacements, then the lowest score.
		bumpable.sort((a, b) => {
			if (a.overlappers.length !== b.overlappers.length) {
				return a.overlappers.length - b.overlappers.length
			}
			return a.score - b.score
		})
		const best = bumpable[0]
		return { nurseId: best.nurse.nurseId, patientsToBump: best.overlappers }
	}

	return null
}

/**
 * Unified patient-to-nurse reassignment.
 *
 * Processes `patientsToMove` in acuity-desc order. For each patient picks the
 * best candidate nurse via scoreIfAssigned (lower is better), enforcing shift
 * fit and 1-to-1 overlap as hard constraints and the settings caps as soft
 * constraints (relaxed on no-fit). When allowBumping is true, will displace
 * non-ongoing, non-1-to-1 patients off a nurse to make room — those displaced
 * patients are re-queued.
 *
 * @param {Object} params
 * @param {Array<Patient>} params.patientsToMove
 * @param {Array<Nurse>} params.candidateNurses
 * @param {{patientSchedule, nurseSchedule, chairSchedule}} params.schedule
 * @param {Object} params.settings - must include maxAcuityForNurse,
 *   maxPatientsForNurse, oneToOneAcuityLevel
 * @param {boolean} params.sandbox
 * @param {boolean} params.isTomorrowMode
 * @param {boolean} [params.allowBumping=false]
 * @returns {Array<{patientId: number, newNurseId: string}>}
 */
const reassignPatients = ({
	patientsToMove,
	candidateNurses,
	schedule,
	settings,
	sandbox,
	isTomorrowMode,
	allowBumping = false
}) => {
	if (!patientsToMove?.length) return []

	const workingSchedule = schedule.patientSchedule.map(p => ({ ...p }))
	const queue = [...patientsToMove].sort((a, b) => b.acuity - a.acuity)
	const bumpedOnce = new Set() // guard against infinite re-bump loops
	const assignments = []

	while (queue.length > 0) {
		const patient = queue.shift()
		const pick = pickNurseFor({
			patient,
			candidateNurses,
			workingSchedule,
			settings,
			sandbox,
			isTomorrowMode,
			allowBumping
		})
		if (!pick) continue // leave unassigned

		const { nurseId, patientsToBump } = pick

		for (const bumped of patientsToBump) {
			if (bumpedOnce.has(bumped.patientId)) continue // don't re-cascade
			bumpedOnce.add(bumped.patientId)
			const wp = workingSchedule.find(p => p.patientId === bumped.patientId)
			if (wp) wp.assignedNurse = null
			queue.push(wp ?? bumped)
		}
		// Resort — newly bumped high-acuity patients should jump to the front.
		queue.sort((a, b) => b.acuity - a.acuity)

		assignments.push({ patientId: patient.patientId, newNurseId: nurseId })
		const wp = workingSchedule.find(p => p.patientId === patient.patientId)
		if (wp) wp.assignedNurse = nurseId
	}

	return assignments
}

/**
 * For rebalancing flows (post-cancellation, post-nurse-add): identify the
 * single most-overloaded nurse — defined as score > average * threshold —
 * and return their highest-acuity movable patient. Patients in excludePatientIds
 * are skipped (used by iterative callers to prevent ping-ponging the same
 * patient between two nurses). Returns null if no nurse is overloaded or no
 * movable patient is available.
 */
export const pickMostOverloadedPatient = ({
	schedule,
	workingSchedule,
	overloadThreshold = 1.25,
	excludePatientIds = new Set()
}) => {
	const ws = workingSchedule ?? schedule.patientSchedule
	const scores = schedule.nurseSchedule.map(nurse => {
		const patients = getNursePatients(ws, nurse.nurseId).filter(
			p => !confirmedCancellationNoShow(p)
		)
		const totalAcuity = patients.reduce((s, p) => s + p.acuity, 0)
		return { nurse, score: totalAcuity + ALPHA * patients.length, patients }
	})
	if (scores.length === 0) return null

	const avg = scores.reduce((s, x) => s + x.score, 0) / scores.length
	const overloaded = scores
		.filter(x => x.score > avg * overloadThreshold)
		.sort((a, b) => b.score - a.score)

	for (const { nurse, patients } of overloaded) {
		const candidate = [...patients]
			.sort((a, b) => b.acuity - a.acuity)
			.find(p => p.internalStatus !== "Ongoing" && !excludePatientIds.has(p.patientId))
		if (candidate) return { sourceNurse: nurse, patient: candidate }
	}
	return null
}

export default reassignPatients
